import { Button, Icon, ToolTip } from '@greystar/designsystem';
import {
  calculateProration,
  getSubTotal,
  useAnalytics,
  useFormattedAddress,
  useFeesAnalytics,
} from '@greystar/lib';
import { EventProperty } from '@greystar/lib/src/types/property-types';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useState } from 'react';
import FeeGroups from '../FeeGroups/FeeGroups';
import FeeLine from '../FeeLine/FeeLine';
import FeeLineItem from '../FeeLineItem/FeeLineItem';
import FeeAccordionProps from './FeeAccordion.props';
import styles from './feeAccordion.module.scss';

const FeeAccordion = (props: FeeAccordionProps) => {
  const [isOpen, setIsOpen] = useState(!props.isSummaryStep);
  const hasFees = props.fees || props.feeMap;
  const { sitecoreContext } = useSitecoreContext();
  const property: EventProperty = sitecoreContext?.property ?? {};
  const propertyId = property.id ?? '';
  const propertyName = property.name ?? '';
  const state = property?.location?.state ?? '';
  const property_location = useFormattedAddress(property.location);

  const { mergeAnalyticsData } = useFeesAnalytics();
  const analytics = useAnalytics();

  const proration = calculateProration(
    props.feeState.selectedBaseRent,
    props.feeState.allFees ?? [],
    props.feeState.numberOfApplicants,
    props.feeState.selectedMoveInDate
  );

  const subtotal = getSubTotal(
    props.feeState.selectedBaseRent,
    props.feeState.numberOfApplicants,
    props.feeState.selectedMoveInDate,
    props.fees,
    props.feeMap ?? new Map(),
    null,
    props.showProration && proration ? proration.feePrice : null
  );

  const subTotalText = props.baseRent ?? subtotal;
  const subTotalNumber = subTotalText?.split('+')[0];
  const variableCosts = subTotalText?.split('+')[1];
  const showProration = props.showProration && proration && proration.feePrice.amount > 0;

  const handleToggle = (title = '') => {
    setIsOpen(!isOpen);

    mergeAnalyticsData({
      overrides: {
        event: 'unit_rent_calc_cost_breakdown_show_hide',
        action_type: 'click',
        section: title,
        click_text: title,
      },
      onComplete: (data) => {
        analytics.trackEvent('unit_rent_calc_cost_breakdown_show_hide', data);
      },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent, title = '') => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      handleToggle(title);
    }
  };

  const updateTooltipData = () => {
    mergeAnalyticsData({
      overrides: {
        event: 'unit_rent_calc_tooltip',
        action_type: 'click',
        tooltip: props.title,
        click_text: props.title,
      },
      onComplete: (data) => {
        analytics.trackEvent('unit_rent_calc_tooltip', data);
      },
    });
  };

  const handleTooltipClick = () => {
    analytics.trackEvent('tooltip', {
      action_type: 'click',
      state: state,
      property_location,
      property_id: propertyId,
      property_name: propertyName,
      content_group: props.title ?? '',
      module_name: 'fees section',
      inpage_location: 'middle',
      click_text: props.title ?? '',
      destination_url: '',
    });
  };

  return (
    <div
      className={`
        ${styles['fee-accordion']}
        ${props.isSummaryStep ? styles['fee-accordion--summary'] : ''}
        ${props.className ?? ''}
      `}
    >
      <div
        className={`${styles['fee-accordion-header']} ${
          isOpen && styles['fee-accordion-header--open']
        }`}
      >
        <div className={styles['fee-accordion-header-inner']}>
          <h6 className={styles['fee-accordion-header-inner-title']}>{props.title}</h6>
          {props.description && (
            <ToolTip
              description={props.description}
              elementToHover={<Icon iconName="QuestionMark" size="sm" />}
              toolTipClick={() => {
                updateTooltipData();
                handleTooltipClick();
              }}
            />
          )}
          {hasFees && (
            <span
              onClick={() => {
                handleToggle(props.title);
              }}
              className={styles['fee-accordion-header-inner-btnContainer']}
            >
              <Button
                onKeyDown={(e) => {
                  handleKeyDown(e, props.title);
                }}
                label={isOpen ? 'Hide' : 'Show'}
                variant="tertiary"
                className={`button button--tertiary ${styles['fee-accordion-header-inner-btnContainer-btn']}`}
              />
              <div
                className={`
                  ${styles['fee-accordion-header-inner-btnContainer-icon']}
                  ${isOpen ? styles['fee-accordion-header-inner-btnContainer-icon--open'] : ''}
                `}
              >
                <Icon iconName="ArrowSmallDown" size="small" />
              </div>
            </span>
          )}
        </div>
        <div className={styles['fee-accordion-header-subtotal']}>
          <span>{subTotalNumber}</span>
          {variableCosts && (
            <span className={styles['fee-accordion-header-subtotal-variable']}>
              + {variableCosts}
            </span>
          )}
        </div>
      </div>
      {isOpen && (
        <div>
          {props.fees?.map((fee) => (
            <FeeLineItem {...props} key={fee.id} fee={fee} isSummaryStep={props.isSummaryStep} />
          ))}
          {props.feeMap && <FeeGroups {...props} feeMap={props.feeMap} />}
          {showProration && <FeeLine proration={proration} {...props} />}
        </div>
      )}
    </div>
  );
};

export default FeeAccordion;
